import { app } from '../utils/firebase';
import { API } from '../api/api';
import { END_POINTS } from '../api/endpoints';
import { toast } from 'react-toastify';

let messaging = null;
let isMessagingSupported = false;
const listeners = new Set();

const PENDING_REVIEWS_STORAGE_KEY = 'oni_ai_cards_pending_reviews';

/**
 * Resolves the redirect path for a notification payload.
 * Extracts consultationId and patientId, and determines mode based on consultationStatus:
 * - If consultationStatus is 'COMPLETED' or 'COMPLETE', mode is 'view'
 * - Otherwise, mode is 'edit'
 *
 * Example:
 * `/consultation/${consultation.id}/patient/${consultation.patientId}/A/history?mode=edit`
 */
export const getNotificationRedirectPath = (data = {}) => {
  if (!data) return '/dashboard';

  const consultationId =
    data.consultationId ||
    data.consultation_id ||
    data.consultation?.id ||
    data.id;

  // const patientId =
  //   data.patientId ||
  //   data.patient_id ||
  //   data.consultation?.patientId ||
  //   data.patient?.id;

  // const rawStatus = (
  //   data.consultationStatus ||
  //   data.consultation_status ||
  //   data.status ||
  //   data.consultation?.status ||
  //   ''
  // ).toString().trim().toUpperCase();

  // const isComplete = rawStatus === 'COMPLETED' || rawStatus === 'COMPLETE';
  // const mode = isComplete ? 'view' : 'edit';

  // if (consultationId && patientId) {
  //   return `/consultation/${consultationId}/patient/${patientId}/A/history?mode=${mode}`;
  // }

  if (consultationId) {
    if (data.route && data.route.includes('{consultationId}')) {
      return data.route.replace('{consultationId}', consultationId);
    }
    return `/consultation/${consultationId}/ai-cards`;
  }

  return data.route || '/dashboard';
};

/**
 * Read stored pending reviews from localStorage
 */
export const getPendingAiReviews = () => {
  try {
    const raw = localStorage.getItem(PENDING_REVIEWS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    console.error('Failed to read pending reviews from storage', err);
    return [];
  }
};

/**
 * Save pending reviews to localStorage and notify all active listeners
 */
export const savePendingAiReviews = (reviews) => {
  try {
    localStorage.setItem(PENDING_REVIEWS_STORAGE_KEY, JSON.stringify(reviews));
  } catch (err) {
    console.error('Failed to save pending reviews to storage', err);
  }
  notifyListeners(reviews);
};

/**
 * Add a new pending review notification
 */
export const addPendingAiReview = (item) => {
  if (!item?.consultationId) return;
  const current = getPendingAiReviews();
  const exists = current.find((r) => r.consultationId === item.consultationId);
  const updatedItem = {
    ...item,
    receivedAt: item.receivedAt || new Date().toISOString(),
  };

  let next;
  if (exists) {
    next = current.map((r) => (r.consultationId === item.consultationId ? { ...r, ...updatedItem } : r));
  } else {
    next = [updatedItem, ...current];
  }
  savePendingAiReviews(next);

  // Dispatch custom window event for in-page subscribers (e.g. VideoCall banner)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('ai_cards_pending_review', {
        detail: updatedItem,
      })
    );
  }
};

/**
 * Dismiss / mark a consultation's pending review as done
 */
export const dismissAiReview = (consultationId) => {
  if (!consultationId) return;
  const current = getPendingAiReviews();
  const next = current.filter((r) => r.consultationId !== consultationId);
  savePendingAiReviews(next);
};

/**
 * Subscribe to pending reviews updates
 */
export const subscribeAiReviewNotifications = (callback) => {
  listeners.add(callback);
  // initial call
  callback(getPendingAiReviews());
  return () => {
    listeners.delete(callback);
  };
};

const notifyListeners = (reviews) => {
  listeners.forEach((callback) => {
    try {
      callback(reviews);
    } catch (err) {
      console.error('Error in AI review notification listener:', err);
    }
  });
};

/**
 * Register device token with backend API (creates/updates Device row for User.id)
 * Safe: only attempts backend registration if an auth token is active.
 */
export const registerDeviceTokenWithBackend = async (token) => {
  if (!token) return { success: false, reason: 'NO_DEVICE_TOKEN' };

  // Only call backend API if user is authenticated with an active session token
  const authToken = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (!authToken) {
    console.log('Skipping device token backend registration: User is not authenticated yet');
    return { success: false, reason: 'UNAUTHENTICATED' };
  }

  try {
    if (END_POINTS.registerDeviceToken) {
      const response = await API.put(END_POINTS.registerDeviceToken, {
        deviceToken: token,
        notificationTurnedOn: true
      });

      // Handle Axios error or bad status
      if (response instanceof Error || response?.isAxiosError || response?.status >= 400 || response?.error) {
        console.warn('Could not register device token with backend:', response?.message || response);
        return { success: false, error: response };
      }

      console.log('FCM Device token successfully registered with backend');
      return { success: true, response };
    }
    return { success: false, reason: 'NO_ENDPOINT' };
  } catch (error) {
    console.warn('Could not register device token with backend:', error);
    return { success: false, error };
  }
};

let serviceWorkerRegistration = null;

/**
 * Initialize FCM messaging, register Service Worker, and listen for foreground messages
 */
export const initFcmNotifications = async () => {
  if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
    console.log('Push notifications not supported in this environment');
    return null;
  }

  try {
    const { getMessaging, isSupported, onMessage } = await import('firebase/messaging');
    const supported = await isSupported();
    if (!supported || !app) {
      console.log('Firebase messaging is not supported in this browser or app config missing');
      return null;
    }

    messaging = getMessaging(app);
    isMessagingSupported = true;

    // Register service worker if not already registered
    try {
      serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      if (serviceWorkerRegistration) {
        await serviceWorkerRegistration.update();
      }
      console.log('Firebase Service Worker registered successfully:', serviceWorkerRegistration);
    } catch (swErr) {
      console.warn('Service worker registration failed:', swErr);
    }

    // Set up foreground message listener
    onMessage(messaging, (payload) => {
      console.log('Foreground FCM message received:', payload);
      const data = payload?.data || {};

      const title = payload.notification?.title || data.title || 'AI Consultation Alert';
      const patientName = data.patientName || '';
      const defaultBody = patientName
        ? `Review and confirm notes from ${patientName}'s consultation`
        : (data.message || 'New consultation update is ready for review');
      const body = payload.notification?.body || data.body || defaultBody;
      const targetRoute = getNotificationRedirectPath(data);

      // 1. If this is an AI review event or consultation notes, update the in-app banner store
      if (data?.eventType === 'ai_cards_pending_review' || data?.consultationId) {
        const item = {
          consultationId: data.consultationId,
          patientId: data.patientId,
          patientName: data.patientName || 'Patient',
          consultationStatus: data.consultationStatus || 'IN_PROGRESS',
          route: targetRoute,
          title,
          body,
          receivedAt: new Date().toISOString(),
        };

        addPendingAiReview(item);
      }

      // 2. ALWAYS display a desktop system notification on the laptop (Notification Center)
      showSystemDesktopNotification({
        title,
        body,
        route: targetRoute,
        data: {
          ...data,
          route: targetRoute,
        },
        tag: data.consultationId ? `ai-cards-${data.consultationId}` : (payload.messageId || `gynac-notif-${Date.now()}`),
      });
    });

    return messaging;
  } catch (error) {
    console.error('Error initializing FCM messaging:', error);
    return null;
  }
};

/**
 * Request notification permission and get FCM registration token
 */
export const requestFcmPermissionAndRegister = async () => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    console.warn('Notifications not supported by this browser');
    return { success: false, reason: 'NOT_SUPPORTED' };
  }

  try {
    console.log('Current notification permission status:', Notification.permission);
    const permission = await Notification.requestPermission();
    console.log('Notification permission result:', permission);

    if (permission !== 'granted') {
      console.warn('Notification permission was not granted by user:', permission);
      return { success: false, reason: 'PERMISSION_DENIED', permission };
    }

    if (!messaging) {
      await initFcmNotifications();
    }

    if (!messaging) {
      console.warn('Firebase Messaging unavailable');
      return { success: false, reason: 'MESSAGING_UNAVAILABLE' };
    }

    const { getToken } = await import('firebase/messaging');

    // Retrieve active service worker registration or wait for ready
    let swReg = serviceWorkerRegistration;
    if (!swReg && 'serviceWorker' in navigator) {
      try {
        swReg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
        if (!swReg) {
          swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        }
      } catch (swErr) {
        console.warn('Could not register or obtain service worker registration:', swErr);
      }
    }

    const tokenOptions = {};
    if (swReg) {
      tokenOptions.serviceWorkerRegistration = swReg;
    }
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY?.trim();
    if (vapidKey) {
      tokenOptions.vapidKey = vapidKey;
    }

    console.log('Requesting FCM token with options:', {
      hasSw: Boolean(swReg),
      hasVapidKey: Boolean(vapidKey),
    });

    const currentToken = await getToken(messaging, tokenOptions);

    if (currentToken) {
      console.log('✅ FCM Device Token obtained successfully:', currentToken);
      localStorage.setItem('fcm_device_token', currentToken);
      const regResult = await registerDeviceTokenWithBackend(currentToken);
      return { success: true, token: currentToken, registered: regResult?.success };
    }

    console.warn('Firebase getToken returned an empty token');
    return { success: false, reason: 'NO_TOKEN_OBTAINED' };
  } catch (error) {
    console.error('❌ Error requesting FCM permission / token:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Quietly syncs FCM device token with backend if browser notification permission
 * is already 'granted' and the user is authenticated. Does NOT prompt user.
 */
export const syncFcmDeviceTokenIfPermitted = async () => {
  if (typeof window === 'undefined' || !('Notification' in window)) return null;
  if (Notification.permission !== 'granted') return null;

  const authToken = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (!authToken) return null;

  try {
    if (!messaging) {
      await initFcmNotifications();
    }
    if (!messaging) return null;

    const { getToken } = await import('firebase/messaging');
    let swReg = serviceWorkerRegistration;
    if (!swReg && 'serviceWorker' in navigator) {
      try {
        swReg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
        if (!swReg) {
          swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        }
      } catch (e) {
        // ignore
      }
    }

    const tokenOptions = {};
    if (swReg) tokenOptions.serviceWorkerRegistration = swReg;
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY?.trim();
    if (vapidKey) tokenOptions.vapidKey = vapidKey;

    const currentToken = await getToken(messaging, tokenOptions);
    if (currentToken) {
      localStorage.setItem('fcm_device_token', currentToken);
      await registerDeviceTokenWithBackend(currentToken);
      return currentToken;
    }
    return null;
  } catch (err) {
    console.warn('Could not quietly sync FCM device token:', err);
    return null;
  }
};

/**
 * Display a system OS desktop notification (shown in macOS / Windows Notification Center)
 * and bind click action to focus window and navigate.
 */
export const showSystemDesktopNotification = async ({ title, body, route, data = {}, tag }) => {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') {
    console.log('Skipping desktop notification: permission is', Notification.permission);
    return;
  }

  const targetRoute = route || getNotificationRedirectPath(data);
  const fullTargetUrl = typeof window !== 'undefined' ? new URL(targetRoute, window.location.origin).href : targetRoute;

  const options = {
    body: body || '',
    icon: '/logo192.png',
    badge: '/favicon.ico',
    tag: tag || `gynac-notif-${Date.now()}`,
    data: {
      ...data,
      route: targetRoute,
      url: fullTargetUrl,
    },
    requireInteraction: true,
  };

  // In-app visual alert so user sees it even if macOS Notification Center silences Chrome
  if (typeof window !== 'undefined') {
    try {
      const toastContent = (
        <div style={{ cursor: 'pointer' }}>
          <strong>{title || 'Notification'}</strong>
          <div style={{ fontSize: 13, opacity: 0.9 }}>{body || ''}</div>
        </div>
      );
      toast.info(toastContent, {
        toastId: tag || undefined,
        autoClose: 7000,
        onClick: () => {
          if (targetRoute && (window.location.pathname + window.location.search) !== targetRoute) {
            window.location.href = targetRoute;
          }
        },
      });
    } catch (e) {
      // ignore
    }
  }

  // 1. Try ServiceWorkerRegistration.showNotification first
  // This produces a true OS-level persistent notification handled by Service Worker notificationclick
  let swReg = serviceWorkerRegistration;
  if (!swReg && 'serviceWorker' in navigator) {
    try {
      swReg = await navigator.serviceWorker.ready;
    } catch (e) {
      // ignore
    }
  }
  if (!swReg && 'serviceWorker' in navigator) {
    try {
      swReg = await navigator.serviceWorker.getRegistration();
    } catch (e) {
      // ignore
    }
  }

  let shown = false;
  if (swReg && typeof swReg.showNotification === 'function') {
    try {
      await swReg.showNotification(title || 'Notification', options);
      console.log('✅ ServiceWorker showNotification succeeded');
      shown = true;
    } catch (err) {
      console.warn('SW showNotification failed, trying fallback:', err);
    }
  }

  // 2. Fallback to native window Notification constructor
  if (!shown) {
    try {
      const notification = new Notification(title || 'Notification', options);
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        if (targetRoute) {
          if ((window.location.pathname + window.location.search) !== targetRoute) {
            window.location.href = targetRoute;
          }
        }
        notification.close();
      };
      console.log('✅ Window Notification created successfully');
    } catch (err) {
      console.error('Error displaying window desktop notification:', err);
    }
  }
};

/**
 * Testing helper: simulate receiving an AI cards pending notification
 * (e.g. against reference job cmv7vwzoe006w or after video call ends)
 */
export const simulateAiCardsNotification = (overrides = {}) => {
  const patientId = overrides.patientId || 'cmv7vi09y004ms601k0bep46n';
  const consultationId = overrides.consultationId || 'cmv7vnbln005us601fy4unys8';
  const consultationStatus = overrides.consultationStatus || 'IN_PROGRESS';
  const patientName = overrides.patientName || 'Priya Sharma';

  const defaultRoute = getNotificationRedirectPath({
    patientId,
    consultationId,
    consultationStatus,
    route: overrides.route,
  });

  const mockPayload = {
    eventType: 'ai_cards_pending_review',
    patientId,
    patientName,
    consultationId,
    consultationStatus,
    route: overrides.route || defaultRoute,
    title: overrides.title || 'AI-extracted notes ready to review',
    body: overrides.body || `Review and confirm notes from ${patientName}'s consultation`,
    receivedAt: new Date().toISOString(),
  };

  addPendingAiReview(mockPayload);

  if (overrides.showDesktop !== false) {
    showSystemDesktopNotification({
      title: mockPayload.title,
      body: mockPayload.body,
      route: mockPayload.route,
      tag: `ai-cards-${mockPayload.consultationId}`,
      data: mockPayload,
    });
  }

  return mockPayload;
};

// Set up global service worker message listener so notification clicks route cleanly
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'NOTIFICATION_CLICKED') {
      const route = event.data?.route || getNotificationRedirectPath(event.data?.data);
      console.log('Notification click action triggered from Service Worker:', route);
      if (typeof window !== 'undefined') {
        window.focus();
        if (route && (window.location.pathname + window.location.search) !== route) {
          window.location.href = route;
        }
      }
    }
  });
}

// Expose testing helper on window for immediate verification in DevTools Console
if (typeof window !== 'undefined') {
  window.testDesktopNotification = async (overrides = {}) => {
    console.log('--- TESTING DESKTOP NOTIFICATION ---');
    console.log('1. Notification API available:', 'Notification' in window);
    console.log('2. Current Notification.permission:', window.Notification ? window.Notification.permission : 'N/A');
    if (window.Notification && window.Notification.permission !== 'granted') {
      const p = await window.Notification.requestPermission();
      console.log('Permission request result:', p);
    }
    const title = overrides.title || 'Oni-Cares AI Alerts Active';
    const body = overrides.body || 'Testing notification on your laptop notification center.';
    await showSystemDesktopNotification({
      title,
      body,
      route: overrides.route || '/dashboard',
    });
    console.log('Desktop notification triggered! Check macOS Notification Center.');
  };
}
